import { Fragment, useEffect, useRef, useState } from 'react'
import { useLang } from '../i18n/LangProvider'
import { useAsistenciaApi, ApiError } from '../asistencia/api'
import { t, Lang, PILLS_BAJA, PillBaja } from './i18n'
import { JornadaDetalle, JornadaResumen, DashboardMiembro, Participacion, duracionPartido, aplicarReglasParticipacion } from './model'
import { PanelEstadisticasJugador } from './PanelEstadisticasJugador'
import { DashboardMinutaje } from './DashboardMinutaje'
import { PILL_COLOR } from './pillColors'
import styles from './MinutajeTab.module.css'
import { Spinner } from '../styles/Spinner'
import { EmptyState } from '../styles/EmptyState'
import { formatDate, localeFor } from '../utils/format'

interface Equipo {
  id: string
  categoria: string
  minutos_por_periodo: number
  num_periodos: number
}

function resultadoJornada(golesFavor: number, golesContra: number): 'V' | 'E' | 'D' {
  if (golesFavor > golesContra) return 'V'
  if (golesFavor < golesContra) return 'D'
  return 'E'
}

const RESULTADO_COLOR: Record<'V' | 'E' | 'D', string> = {
  V: 'var(--color-success)',
  E: 'var(--color-neutral)',
  D: 'var(--color-danger)',
}

function pillStyle(pressed: boolean, color: string) {
  return pressed ? { backgroundColor: color } : undefined
}

export function MinutajeTab({
  equipo,
  lang: langProp,
  readOnly = false,
}: {
  equipo: Equipo
  lang?: Lang
  readOnly?: boolean
}) {
  const ctxLang = useLang()
  const lang = langProp ?? ctxLang
  const api = useAsistenciaApi()
  const duracion = duracionPartido(equipo)
  // Issue #62: tarjetas y ciclo de sanción solo en F11.
  const esF11 = equipo.categoria === 'f11'
  const locale = localeFor(lang)

  const [vista, setVista] = useState<'jornada' | 'dashboard'>('jornada')
  const [numero, setNumero] = useState(1)
  const formRef = useRef<HTMLElement>(null)
  const [jornada, setJornada] = useState<JornadaDetalle | null>(null)
  const [historial, setHistorial] = useState<JornadaResumen[]>([])
  const [dashboard, setDashboard] = useState<DashboardMiembro[] | null>(null)
  const [dashboardError, setDashboardError] = useState(false)
  const [error, setError] = useState<'forbidden' | 'other' | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  async function refreshHistorial(): Promise<JornadaResumen[] | null> {
    try {
      const res: JornadaResumen[] = await api.get(`/equipos/${equipo.id}/jornadas`)
      setHistorial(res)
      setError(null)
      return res
    } catch (err) {
      setError(err instanceof ApiError && err.status === 403 ? 'forbidden' : 'other')
      return null
    }
  }

  async function loadJornada(n: number) {
    // `numero` se actualiza siempre (éxito o fallo), para que "Reintentar" tras un error vuelva a
    // pedir la jornada correcta, no la última que se había cargado con éxito.
    setNumero(n)
    try {
      const res: JornadaDetalle = await api.get(`/equipos/${equipo.id}/jornadas/${n}`)
      setJornada(res)
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError && err.status === 403 ? 'forbidden' : 'other')
    }
  }

  async function abrirJornadaHistorial(n: number) {
    await loadJornada(n)
    formRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }

  useEffect(() => {
    // Bug real, feedback del cliente 2026-10-03: `refreshHistorial` y `loadJornada` escribían el
    // mismo estado `error` en paralelo (sin `await` entre ellas); si una fallaba y la otra tenía
    // éxito después, la que tenía éxito borraba el error de la que había fallado — a veces se veía
    // un spinner infinito sin aviso, otras veces se ocultaba un fallo real de `loadJornada`. Ahora
    // se ejecutan en secuencia.
    async function cargarInicial() {
      const historialInicial = await refreshHistorial()
      // ISSUE 4 del club, 2026-10-03 ("al empezar una jornada nueva, el campo se rellena solo con
      // el siguiente número"): antes siempre partía de la jornada 1, obligando a teclear a mano el
      // número de la siguiente jornada cada vez. Ahora, si ya hay jornadas guardadas, propone la
      // siguiente (máximo existente + 1); el historial sigue permitiendo reabrir cualquier jornada
      // anterior con un clic.
      const siguienteNumero =
        historialInicial && historialInicial.length > 0
          ? Math.max(...historialInicial.map((j) => j.numero)) + 1
          : numero
      await loadJornada(siguienteNumero)
    }
    cargarInicial()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipo.id])

  function cargarDashboard() {
    setDashboardError(false)
    api
      .get(`/equipos/${equipo.id}/dashboard`)
      .then(setDashboard)
      .catch(() => setDashboardError(true))
  }

  useEffect(() => {
    // Bug real, feedback del cliente 2026-10-03: "no veo las estadísticas" — esta petición no
    // capturaba errores; si fallaba (sesión expirada, red, etc.), `dashboard` se quedaba en `null`
    // para siempre sin ningún aviso, y la pestaña Dashboard se quedaba en blanco silenciosamente.
    if (vista === 'dashboard' && !dashboard && !dashboardError) {
      cargarDashboard()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vista])

  if (error === 'forbidden') {
    return <p>{t('minutaje', lang)}</p>
  }
  if (error === 'other') {
    // No mostrar los datos de la jornada anterior como si fueran los de `numero` — induciría a
    // error (bug real del cliente, 2026-10-02: creía que no podía "abrir" la jornada porque, al
    // fallar la carga, no había ningún aviso ni forma de reintentar, solo un spinner infinito).
    return (
      <div>
        <p>{t('errorGenerico', lang)}</p>
        <button onClick={() => loadJornada(numero)}>{t('reintentar', lang)}</button>
      </div>
    )
  }
  if (!jornada) return <Spinner />

  function updateJornadaField<K extends keyof JornadaDetalle>(key: K, value: JornadaDetalle[K]) {
    setJornada((prev) => (prev ? { ...prev, [key]: value } : prev))
  }

  function updateParticipacion(miembroId: string, cambio: Partial<Participacion>) {
    setJornada((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        participaciones: prev.participaciones.map((p) => {
          if (p.miembro_equipo_id !== miembroId) return p
          const actualizado = { ...p, ...cambio }
          return aplicarReglasParticipacion(actualizado, duracion)
        }),
      }
    })
  }

  const jornadaActual = jornada

  function togglePillEstado(miembroId: string, campo: 'convocado' | 'jugado' | 'titular') {
    if (readOnly) return
    const p = jornadaActual.participaciones.find((x) => x.miembro_equipo_id === miembroId)
    if (!p) return
    updateParticipacion(miembroId, { [campo]: !p[campo] } as Partial<Participacion>)
  }

  function togglePillBaja(miembroId: string, baja: PillBaja) {
    if (readOnly) return
    const p = jornadaActual.participaciones.find((x) => x.miembro_equipo_id === miembroId)
    if (!p) return
    updateParticipacion(miembroId, { baja: p.baja === baja ? null : baja })
  }

  function setMinutos(miembroId: string, minutos: number) {
    updateParticipacion(miembroId, { minutos })
  }

  function setGoles(miembroId: string, goles: number) {
    updateParticipacion(miembroId, { goles })
  }

  function setTarjetas(miembroId: string, tarjetas: { amarillas?: number; rojas?: number }) {
    updateParticipacion(miembroId, tarjetas)
  }

  async function handleGuardar() {
    try {
      await api.post(`/equipos/${equipo.id}/jornadas`, {
        numero: jornadaActual.numero,
        rival: jornadaActual.rival ?? undefined,
        fecha: jornadaActual.fecha ?? undefined,
        campo: jornadaActual.campo ?? 'local',
        goles_favor: jornadaActual.goles_favor,
        goles_contra: jornadaActual.goles_contra,
        participaciones: jornadaActual.participaciones.map((p) => ({
          miembro_equipo_id: p.miembro_equipo_id,
          convocado: p.convocado,
          jugado: p.jugado,
          titular: p.titular,
          baja: p.baja,
          minutos: p.minutos,
          goles: p.goles,
          amarillas: p.amarillas ?? 0,
          rojas: p.rojas ?? 0,
        })),
      })
      await refreshHistorial()
      await loadJornada(jornadaActual.numero)
      // Bug real del club, 2026-10-02 (feedback por vídeo): "le pongo 90, un gol, titular... ya
      // está, minutos jugados 90, y no sale aquí [en el dashboard]". El dashboard se cacheaba en
      // `dashboard` y solo se recargaba la primera vez que se abría esa pestaña (`!dashboard` en el
      // useEffect de abajo) — tras guardar una jornada nueva, si el dashboard ya se había visitado
      // antes en esta sesión, quedaba con los datos de antes de guardar hasta recargar la página
      // entera. Se invalida aquí para que la próxima vez que se abra la pestaña Dashboard, el
      // useEffect (`!dashboard`) vuelva a pedirlo fresco.
      setDashboard(null)
      setDashboardError(false)
    } catch (err) {
      // Antes mostraba siempre el mismo mensaje genérico, sin importar la causa real (p. ej.
      // minutos fuera del margen permitido, o temporada cerrada) — igual criterio que el resto de
      // la app: mostrar el mensaje real del backend cuando lo hay.
      window.alert(err instanceof Error ? err.message : t('guardarJornada', lang))
    }
  }

  const convocados = jornada.participaciones.filter((p) => p.convocado).length
  const jugados = jornada.participaciones.filter((p) => p.jugado).length

  const duracionLabel =
    equipo.num_periodos === 2
      ? `2×${equipo.minutos_por_periodo} min · ${t('duracionPartido', lang)}`
      : `3×${equipo.minutos_por_periodo} min (${duracion} min)`

  // Opciones del selector de jornada: de la 1 a la siguiente por crear (máximo guardado + 1, o la que
  // esté abierta si es mayor), la más reciente primero.
  const historialPorNumero = new Map(historial.map((j) => [j.numero, j]))
  const siguienteJornada = historial.length > 0 ? Math.max(...historial.map((j) => j.numero)) + 1 : 1
  const opcionesJornada = Array.from({ length: Math.max(siguienteJornada, numero) }, (_, k) => k + 1).reverse()

  return (
    <div>
      <nav className="tabs">
        <button onClick={() => setVista('jornada')} disabled={vista === 'jornada'}>
          {t('jornadaTab', lang)}
        </button>
        <button onClick={() => setVista('dashboard')} disabled={vista === 'dashboard'}>
          {t('dashboardTab', lang)}
        </button>
      </nav>

      {vista === 'dashboard' && dashboardError && (
        <div>
          <p>{t('errorGenerico', lang)}</p>
          <button onClick={cargarDashboard}>{t('reintentar', lang)}</button>
        </div>
      )}

      {vista === 'dashboard' && !dashboard && !dashboardError && <Spinner />}

      {vista === 'dashboard' && dashboard && (
        <DashboardMinutaje
          equipoId={equipo.id}
          dashboard={dashboard}
          historial={historial}
          duracionLabel={duracionLabel}
          lang={lang}
          conTarjetas={esF11}
        />
      )}

      {vista === 'jornada' && (
        <>
          <section className={styles.jornadaForm} ref={formRef}>
            {/* Look & feel (2026-10-22): "el selector de jornada es un numérico, pero no pone quiénes
                juegan; abajo, en el historial, sí sale el rival". Desplegable con las jornadas del 1
                a la siguiente por crear, la más reciente arriba: las guardadas con rival, fecha y
                resultado; los huecos sin registrar siguen pudiéndose elegir. */}
            <label>
              {t('jornadaNumero', lang)}
              <select value={numero} onChange={(e) => loadJornada(Number(e.target.value))}>
                {opcionesJornada.map((n) => {
                  const j = historialPorNumero.get(n)
                  return (
                    <option key={n} value={n}>
                      {j
                        ? `J${n} · ${j.rival ?? '—'}${j.fecha ? ` · ${formatDate(j.fecha, locale)}` : ''} · ${resultadoJornada(j.goles_favor, j.goles_contra)} ${j.goles_favor}-${j.goles_contra}`
                        : n === siguienteJornada
                          ? `J${n} · ${t('nuevaJornada', lang)}`
                          : `J${n} · ${t('sinRegistrar', lang)}`}
                    </option>
                  )
                })}
              </select>
            </label>
            <label>
              {t('rival', lang)}
              <input value={jornada.rival ?? ''} onChange={(e) => updateJornadaField('rival', e.target.value)} />
            </label>
            <label>
              {t('fecha', lang)}
              <input
                type="date"
                value={jornada.fecha ? jornada.fecha.slice(0, 10) : ''}
                onChange={(e) => updateJornadaField('fecha', e.target.value)}
              />
            </label>
            <label>
              {t('campo', lang)}
              <select value={jornada.campo ?? 'local'} onChange={(e) => updateJornadaField('campo', e.target.value as 'local' | 'visitante')}>
                <option value="local">{t('local', lang)}</option>
                <option value="visitante">{t('visitante', lang)}</option>
              </select>
            </label>
            <label>
              {t('golesFavor', lang)}
              <input
                type="number"
                min={0}
                value={jornada.goles_favor}
                onFocus={(e) => e.target.select()}
                onChange={(e) => updateJornadaField('goles_favor', Number(e.target.value))}
              />
            </label>
            <label>
              {t('golesContra', lang)}
              <input
                type="number"
                min={0}
                value={jornada.goles_contra}
                onFocus={(e) => e.target.select()}
                onChange={(e) => updateJornadaField('goles_contra', Number(e.target.value))}
              />
            </label>
            <label className={styles.duracionField}>
              {t('duracion', lang)}
              <span aria-label="duracion-partido">{duracionLabel}</span>
            </label>
          </section>

          <p>
            {convocados} {t('convocadosContador', lang).split(' · ')[0]} · {jugados} {t('convocadosContador', lang).split(' · ')[1]}
          </p>

          <div className={styles.tableWrap}>
          <table className={styles.table}>
            <tbody>
              {jornada.participaciones.map((p, idx) => (
                <Fragment key={p.miembro_equipo_id}>
                <tr className={p.convocado ? undefined : styles.noConvocadoRow}>
                  <td className={styles.numeroCell}>{idx + 1}</td>
                  <td className={styles.nombreCell}>
                    <button onClick={() => setExpandedId(expandedId === p.miembro_equipo_id ? null : p.miembro_equipo_id)}>
                      {p.persona.nombre}
                    </button>
                    <button
                      aria-label="stats-jugador"
                      onClick={() => setExpandedId(expandedId === p.miembro_equipo_id ? null : p.miembro_equipo_id)}
                    >
                      📊
                    </button>
                    {/* Feedback del cliente, 2026-10-05: un jugador no convocado mostraba todo a
                        0/en blanco, indistinguible de "la jornada no ha cargado" — se confundía con
                        un fallo real. Etiqueta explícita en vez de solo dejarlo en blanco. */}
                    {!p.convocado && <span className={styles.noConvocadoBadge}>{t('noConvocado', lang)}</span>}
                    {esF11 && p.sancionado && (
                      <span className={styles.sancionadoBadge} aria-label="sancionado">
                        🟨×5 {t('sancionado', lang)}
                      </span>
                    )}
                  </td>
                  <td className={styles.pillsCell}>
                    <button
                      aria-pressed={p.convocado}
                      disabled={readOnly}
                      className={`${styles.pill} ${p.convocado ? styles.pillActive : ''}`}
                      style={pillStyle(p.convocado, PILL_COLOR.conv)}
                      onClick={() => togglePillEstado(p.miembro_equipo_id, 'convocado')}
                    >
                      {t('conv', lang)}
                    </button>
                    <button
                      aria-pressed={p.jugado}
                      disabled={readOnly}
                      className={`${styles.pill} ${p.jugado ? styles.pillActive : ''}`}
                      style={pillStyle(p.jugado, PILL_COLOR.jug)}
                      onClick={() => togglePillEstado(p.miembro_equipo_id, 'jugado')}
                    >
                      {t('jug', lang)}
                    </button>
                    <button
                      aria-pressed={p.titular}
                      disabled={readOnly}
                      className={`${styles.pill} ${p.titular ? styles.pillActive : ''}`}
                      style={pillStyle(p.titular, PILL_COLOR.tit)}
                      onClick={() => togglePillEstado(p.miembro_equipo_id, 'titular')}
                    >
                      {t('tit', lang)}
                    </button>
                    {PILLS_BAJA.map((baja) => (
                      <button
                        key={baja}
                        aria-pressed={p.baja === baja}
                        disabled={readOnly}
                        className={`${styles.pill} ${p.baja === baja ? styles.pillActive : ''}`}
                        style={pillStyle(p.baja === baja, PILL_COLOR[baja])}
                        onClick={() => togglePillBaja(p.miembro_equipo_id, baja)}
                      >
                        {t(baja.toLowerCase() as 'les' | 'san' | 'enf' | 'vac' | 'tr' | 'nj', lang)}
                      </button>
                    ))}
                  </td>
                  <td className={styles.minutosCell}>
                    <label>
                      {t('minutos', lang)}
                      <input
                        type="number"
                        min={0}
                        value={p.minutos}
                        disabled={readOnly}
                        // Issue #63 del club, 2026-10-06: "muestra un cero inicial que resulta
                        // molesto al editar... permitir que los minutos se puedan escribir y
                        // borrar fácilmente, sin saltos raros del cursor". Seleccionar todo el
                        // contenido al enfocar hace que el primer dígito escrito sustituya al 0
                        // en vez de concatenarse con él.
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => setMinutos(p.miembro_equipo_id, Number(e.target.value))}
                      />
                    </label>
                    {/* ISSUE 4 del club, 2026-10-03: "% de minutos de cada jugador = minutos
                        jugados / duración del partido" — no existía en el detalle de la jornada. */}
                    <span aria-label="pct-minutos">
                      {duracion > 0 ? Math.round((p.minutos / duracion) * 100) : 0}%
                    </span>
                    <button disabled={readOnly} onClick={() => setMinutos(p.miembro_equipo_id, equipo.minutos_por_periodo)}>
                      {t('unPeriodo', lang)}
                    </button>
                    <button disabled={readOnly} onClick={() => setMinutos(p.miembro_equipo_id, duracion)}>{t('partidoCompleto', lang)}</button>
                    {equipo.num_periodos === 3 && (
                      <button disabled={readOnly} onClick={() => setMinutos(p.miembro_equipo_id, equipo.minutos_por_periodo * 3)}>
                        {t('tresPeriodos', lang)}
                      </button>
                    )}
                    <label>
                      {t('goles', lang)}
                      <input
                        type="number"
                        min={0}
                        value={p.goles}
                        disabled={readOnly}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => setGoles(p.miembro_equipo_id, Number(e.target.value))}
                      />
                    </label>
                    {esF11 && (
                      <>
                        <label className={styles.tarjetaField}>
                          <span aria-hidden>🟨</span>
                          <input
                            type="number"
                            min={0}
                            max={2}
                            aria-label={t('amarillas', lang)}
                            value={p.amarillas ?? 0}
                            disabled={readOnly}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => setTarjetas(p.miembro_equipo_id, { amarillas: Number(e.target.value) })}
                          />
                        </label>
                        <label className={styles.tarjetaField}>
                          <span aria-hidden>🟥</span>
                          <input
                            type="number"
                            min={0}
                            max={1}
                            aria-label={t('rojas', lang)}
                            value={p.rojas ?? 0}
                            disabled={readOnly}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => setTarjetas(p.miembro_equipo_id, { rojas: Number(e.target.value) })}
                          />
                        </label>
                      </>
                    )}
                  </td>
                </tr>
                {expandedId === p.miembro_equipo_id && (
                  <tr className={styles.statsRow}>
                    <td colSpan={10}>
                      <PanelEstadisticasJugador
                        equipoId={equipo.id}
                        miembroEquipoId={p.miembro_equipo_id}
                        lang={lang}
                        conTarjetas={esF11}
                      />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
          </div>

          {!readOnly && (
            <div className={styles.saveBar}>
              <button onClick={handleGuardar}>{t('guardarJornada', lang)}</button>
            </div>
          )}

          <section>
            <h3>
              {t('historial', lang)} · {historial.length} {t('registradas', lang)}
            </h3>
            {historial.length === 0 && <EmptyState text={t('sinJornadas', lang)} icon="📅" />}
            {historial.map((j) => {
              const resultado = resultadoJornada(j.goles_favor, j.goles_contra)
              return (
                // Feedback del club (2026-10-11, móvil): al tocar una jornada del historial se cargaba en el
                // formulario de arriba, miles de px por encima, sin ninguna señal — parecía que no hacía
                // nada. Ahora la tarjeta cargada queda marcada y se sube al formulario.
                <article
                  key={j.id}
                  className={`${styles.jornadaCard} ${j.numero === numero ? styles.jornadaCardActiva : ''}`}
                  aria-current={j.numero === numero ? 'true' : undefined}
                  role="button"
                  tabIndex={0}
                  onClick={() => abrirJornadaHistorial(j.numero)}
                  onKeyDown={(e) => e.key === 'Enter' && abrirJornadaHistorial(j.numero)}
                >
                  <span className={styles.resultado} style={{ backgroundColor: RESULTADO_COLOR[resultado] }}>
                    {resultado}
                  </span>
                  <span>{j.rival}</span>
                  <span>{j.fecha ? formatDate(j.fecha, locale) : ''}</span>
                </article>
              )
            })}
          </section>
        </>
      )}
    </div>
  )
}
